import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import { Link } from 'react-router-dom';
import ApplicationRequired from '../../components/common/ApplicationRequired';
import {
  ClipboardDocumentIcon,
  CheckIcon,
  CodeBracketIcon,
  CommandLineIcon,
  CubeIcon,
  ArrowTopRightOnSquareIcon,
  BookOpenIcon,
  AcademicCapIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import MainLayout from '../../components/layout/MainLayout';
import sdkService from '../../services/api/sdk.service';

export default function SDKSetupPage() {
  const { t } = useTranslation('analytics');
  const navigate = useNavigate();
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const isAllAppsSelected = !selectedAppId || selectedAppId === 'all';
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [installMethod, setInstallMethod] = useState<'npm' | 'cdn'>('cdn');
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [appId, setAppId] = useState('');
  const [apiEndpoint, setApiEndpoint] = useState('');
  const [previousAppId, setPreviousAppId] = useState<string | null>(null);

  // SDK Configuration States
  const [config, setConfig] = useState({
    sessionRecording: {
      enabled: true,
      maskAllInputs: true,
      sampling: 1.0,
    },
    heatmap: {
      enabled: true,
      trackClicks: true,
      trackScroll: true,
      trackMouseMovement: true,
      mouseMoveSampling: 0.1,
    },
    performance: {
      captureWebVitals: true,
      captureNetworkRequests: true,
    },
    errors: {
      captureErrors: true,
    },
    privacy: {
      respectDoNotTrack: true,
      cookieConsent: false,
    },
  });

  // Load SDK configuration from backend
  useEffect(() => {
    const loadSDKConfig = async () => {
      if (!selectedAppId) {
        setLoading(false);
        setApiKey('');
        setAppId('');
        return;
      }

      try {
        setLoading(true);
        const sdkConfig = await sdkService.getSDKConfiguration(selectedAppId);
        setApiKey(sdkConfig.apiKey);
        setAppId(sdkConfig.appId);
        setApiEndpoint(sdkConfig.apiEndpoint);
      } catch (error) {
        console.error('Failed to load SDK configuration:', error);
        setApiKey('');
        setAppId('');
      } finally {
        setLoading(false);
      }
    };

    loadSDKConfig();
  }, [selectedAppId]);

  // Monitor global application changes
  useEffect(() => {
    if (isAllAppsSelected && previousAppId) {
      // User switched to "All Applications"
      toast.error(
        t('errors.cannotUseAllApps') || 'Cannot configure Browser SDK with "All Applications" selected. Please select a specific application.',
        {
          duration: 6000,
          icon: '⚠️',
        }
      );
    } else if (selectedAppId && selectedAppId !== 'all' && previousAppId && selectedAppId !== previousAppId) {
      // User switched to a different specific application
      toast.success(
        t('info.applicationSwitched') || `Application switched successfully`,
        {
          duration: 3000,
          icon: '🔄',
        }
      );
    }

    // Update previous app ID
    if (selectedAppId !== previousAppId) {
      setPreviousAppId(selectedAppId);
    }
  }, [selectedAppId, isAllAppsSelected, previousAppId, t]);

  const copyToClipboard = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const npmInstall = `npm install @devskin/browser-sdk

# Ou instale diretamente do GitHub
npm install https://github.com/devskin1/devskin-monitoring-browser-sdk.git`;

  // Generate dynamic init code based on config
  const generateInitCode = () => {
    const parts = [
      `  apiKey: '${apiKey}',`,
      `  appId: '${appId}',`,
      `  apiUrl: 'https://api-monitoring.devskin.com',`,
      ''
    ];

    if (config.sessionRecording.enabled) {
      parts.push('  // Session Recording');
      parts.push('  sessionRecording: {');
      parts.push('    enabled: true,');
      parts.push(`    maskAllInputs: ${config.sessionRecording.maskAllInputs},`);
      parts.push(`    sampling: ${config.sessionRecording.sampling}, // Record ${(config.sessionRecording.sampling * 100).toFixed(0)}% of sessions`);
      parts.push('  },');
      parts.push('');
    }

    if (config.heatmap.enabled) {
      parts.push('  // Heatmap & User Behavior');
      parts.push('  heatmapOptions: {');
      parts.push('    enabled: true,');
      parts.push(`    trackClicks: ${config.heatmap.trackClicks},`);
      parts.push(`    trackScroll: ${config.heatmap.trackScroll},`);
      parts.push(`    trackMouseMovement: ${config.heatmap.trackMouseMovement},`);
      if (config.heatmap.trackMouseMovement) {
        parts.push(`    mouseMoveSampling: ${config.heatmap.mouseMoveSampling}, // Sample ${(config.heatmap.mouseMoveSampling * 100).toFixed(0)}% of mouse movements`);
      }
      parts.push('  },');
      parts.push('');
    }

    if (config.performance.captureWebVitals || config.performance.captureNetworkRequests) {
      parts.push('  // Performance Monitoring');
      parts.push(`  captureWebVitals: ${config.performance.captureWebVitals},`);
      parts.push(`  captureNetworkRequests: ${config.performance.captureNetworkRequests},`);
      parts.push('');
    }

    if (config.errors.captureErrors) {
      parts.push('  // Error Tracking');
      parts.push('  captureErrors: true,');
      parts.push('');
    }

    parts.push('  // Privacy');
    parts.push('  privacy: {');
    parts.push(`    respectDoNotTrack: ${config.privacy.respectDoNotTrack},`);
    parts.push(`    cookieConsent: ${config.privacy.cookieConsent},`);
    parts.push('  },');
    parts.push('');
    parts.push('  // Environment');
    parts.push("  environment: 'production',");
    parts.push("  release: '1.0.0',");
    parts.push('  debug: false,');

    return parts.join('\n');
  };

  // Generate CDN install with config inline
  const generateCDNInstall = () => {
    return `<!-- change to <script async> if you want to run async -->
<script>
  (function(w,d,c,a,b){
    // Skip for bots (SEO optimization)
    if(/bot|crawler|spider|lighthouse|pagespeed|gtmetrix|headless/i.test(navigator.userAgent))return;

    // Create DevSkin stub that queues commands
    w.DevSkin=w.DevSkin||function(){(w.DevSkin.q=w.DevSkin.q||[]).push(Array.prototype.slice.call(arguments))};
    w.DevSkin.track=w.DevSkin.track||function(n,p){w.DevSkin.q.push(['track',n,p])};
    w.DevSkin.identify=w.DevSkin.identify||function(u,t){w.DevSkin.q.push(['identify',u,t])};
    w.DevSkin.l=Date.now();

    // Queue init command with config
    w.DevSkin('init',c);

    // Load SDK async
    a=d.getElementsByTagName('head')[0];
    b=d.createElement('script');
    b.async=1;
    b.src='https://cdn.jsdelivr.net/npm/@devskin/browser-sdk@latest/dist/devskin.umd.min.js';
    a.appendChild(b);
  })(window,document,{
${generateInitCode()}
  });
</script>`;
  };

  const cdnInstall = generateCDNInstall();

  const initCode = installMethod === 'npm'
    ? `import DevSkin from '@devskin/browser-sdk';

DevSkin.init({
${generateInitCode()}
});`
    : `<script>
  // Initialize DevSkin (commands are queued until SDK loads)
  DevSkin('init', {
${generateInitCode()}
  });
</script>`;

  const trackEventExample = `// Track custom events (use after SDK loads)
DevSkin.track('button_clicked', {
  button_id: 'signup',
  variant: 'primary',
  price: 29.99,
});

// Identify users
DevSkin.identify('user-123', {
  email: 'user@example.com',
  name: 'John Doe',
  plan: 'pro',
});

// Track page views (automatic by default)
DevSkin('trackPageView');

// Add breadcrumbs for debugging
DevSkin('addBreadcrumb', {
  category: 'navigation',
  message: 'User navigated to checkout',
  level: 'info',
  data: { from: '/cart', to: '/checkout' },
});`;

  const errorTrackingExample = `// Automatic error tracking is enabled by default

// Manual error capture
DevSkin('captureError', new Error('Something went wrong'), {
  tags: { feature: 'checkout', severity: 'high' },
  extra: { cart_value: 299.99, items: 3 },
  level: 'error', // 'fatal', 'error', 'warning', 'info'
});

// Set release version for error tracking
DevSkin('setRelease', '1.0.5');

// Set environment
DevSkin('setEnvironment', 'production');

// GDPR: Opt out/in
DevSkin('optOut');
DevSkin('optIn');`;

  const CodeBlock = ({ code, index, language = 'javascript' }: { code: string; index: number; language?: string }) => (
    <div className="relative group">
      <div className="flex items-center justify-between bg-gray-800 dark:bg-gray-900 px-4 py-2 rounded-t-lg">
        <span className="text-xs font-mono text-gray-400">{language}</span>
        <button
          onClick={() => copyToClipboard(code, index)}
          className="flex items-center space-x-2 text-sm text-gray-400 hover:text-white transition-colors"
        >
          {copiedIndex === index ? (
            <>
              <CheckIcon className="w-4 h-4 text-green-500" />
              <span className="text-green-500">{t('sdk.copied')}</span>
            </>
          ) : (
            <>
              <ClipboardDocumentIcon className="w-4 h-4" />
              <span>{t('sdk.copy')}</span>
            </>
          )}
        </button>
      </div>
      <pre className="bg-gray-900 dark:bg-gray-950 text-gray-100 p-4 rounded-b-lg overflow-x-auto text-sm font-mono shadow-inner">
        <code>{code}</code>
      </pre>
    </div>
  );

  // Show elegant empty state when All Applications is selected
  if (isAllAppsSelected) {
    return (
      <MainLayout>
        <ApplicationRequired
          title={t('validation.title') || 'Application Selection Required'}
          message={t('validation.description') || 'Before configuring the Browser SDK, you must select a specific application. The "All Applications" option cannot be used for SDK configuration.'}
        />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header with gradient */}
        <div className="relative overflow-hidden bg-gradient-to-br from-cyan-500/10 via-blue-500/10 to-purple-500/10 dark:from-cyan-500/5 dark:via-blue-500/5 dark:to-purple-500/5 rounded-2xl p-8 border border-cyan-200/50 dark:border-cyan-800/50">
          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-2">
              <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl shadow-lg">
                <CodeBracketIcon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {t('sdk.title')}
              </h1>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
              {t('sdk.subtitle')}
            </p>
          </div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-cyan-400/20 to-blue-600/20 rounded-full blur-3xl -z-0" />
        </div>

        {!selectedAppId ? (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 p-12 text-center">
            <CodeBracketIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {t('validation.pleaseSelect')}
            </p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
          </div>
        ) : (
          <>

        {/* rrweb Feature Highlight Banner */}
        <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-pink-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                <span className="text-3xl">🎥</span>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-xl font-bold">{t('sdk.rrweb.banner.title')}</h3>
                <span className="text-xs bg-white/30 px-2 py-1 rounded-full backdrop-blur-sm">{t('sdk.rrweb.banner.badge')}</span>
              </div>
              <p className="text-purple-100 text-sm mb-3">
                {t('sdk.rrweb.banner.description')}
              </p>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm">✅ {t('sdk.rrweb.banner.feature1')}</span>
                <span className="bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm">✅ {t('sdk.rrweb.banner.feature2')}</span>
                <span className="bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm">✅ {t('sdk.rrweb.banner.feature3')}</span>
                <span className="bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm">✅ {t('sdk.rrweb.banner.feature4')}</span>
              </div>
            </div>
            <a
              href="https://github.com/devskin1/devskin-monitoring-browser-sdk"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 bg-white text-purple-600 px-4 py-2 rounded-lg font-semibold hover:bg-purple-50 transition-colors flex items-center gap-2 text-sm"
            >
              <span>{t('sdk.rrweb.banner.githubButton')}</span>
              <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Quick Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 border border-cyan-200 dark:border-cyan-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-cyan-500 rounded-lg shadow-md">
                  <CodeBracketIcon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{t('sdk.apiKey')}</h3>
              </div>
              <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded-full">
                Public Key
              </span>
            </div>
            <code className="text-sm font-mono text-cyan-700 dark:text-cyan-300 break-all block bg-white/50 dark:bg-gray-900/50 px-3 py-2 rounded-lg mb-2">
              {apiKey}
            </code>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
              ✓ Safe for frontend use - can only send telemetry data
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 bg-purple-500 rounded-lg shadow-md">
                <CubeIcon className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('sdk.appId')}</h3>
            </div>
            <code className="text-sm font-mono text-purple-700 dark:text-purple-300 break-all block bg-white/50 dark:bg-gray-900/50 px-3 py-2 rounded-lg">
              {appId}
            </code>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 bg-green-500 rounded-lg shadow-md">
                <CommandLineIcon className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">API URL</h3>
            </div>
            <code className="text-sm font-mono text-green-700 dark:text-green-300 break-all block bg-white/50 dark:bg-gray-900/50 px-3 py-2 rounded-lg">
              https://api-monitoring.devskin.com
            </code>
          </div>
        </div>

        {/* SDK Configuration */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-2 mb-6">
            <SparklesIcon className="w-6 h-6 text-cyan-600" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              SDK Configuration
            </h2>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Customize o SDK de acordo com suas necessidades. O código será gerado automaticamente com suas configurações.
          </p>

          <div className="space-y-6">
            {/* Session Recording */}
            <div className="border-l-4 border-purple-500 pl-4 bg-purple-50/50 dark:bg-purple-900/10 py-3 rounded-r-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    🎥 {t('sdk.rrweb.sessionRecording')} <span className="text-xs bg-purple-600 text-white px-2 py-1 rounded-full ml-2">{t('sdk.rrweb.poweredBy')}</span>
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('sdk.rrweb.features.realDom')}
                  </p>
                  <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                    ✨ {t('sdk.rrweb.features.captures')}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.sessionRecording.enabled}
                    onChange={(e) => setConfig({ ...config, sessionRecording: { ...config.sessionRecording, enabled: e.target.checked } })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-300 dark:peer-focus:ring-cyan-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-cyan-600"></div>
                </label>
              </div>

              {config.sessionRecording.enabled && (
                <div className="space-y-3 mt-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={config.sessionRecording.maskAllInputs}
                      onChange={(e) => setConfig({ ...config, sessionRecording: { ...config.sessionRecording, maskAllInputs: e.target.checked } })}
                      className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('sdk.advancedConfig.maskAllInputs')}</span>
                  </label>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sampling Rate: {(config.sessionRecording.sampling * 100).toFixed(0)}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={config.sessionRecording.sampling}
                      onChange={(e) => setConfig({ ...config, sessionRecording: { ...config.sessionRecording, sampling: parseFloat(e.target.value) } })}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('sdk.advancedConfig.recordSessionsSampling', { percent: (config.sessionRecording.sampling * 100).toFixed(0) })}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Heatmaps */}
            <div className="border-l-4 border-orange-500 pl-4 bg-orange-50/50 dark:bg-orange-900/10 py-3 rounded-r-lg">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    🗺️ Heatmaps & User Behavior
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Rastreie cliques, scroll e movimento do mouse
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.heatmap.enabled}
                    onChange={(e) => setConfig({ ...config, heatmap: { ...config.heatmap, enabled: e.target.checked } })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-orange-600"></div>
                </label>
              </div>

              {config.heatmap.enabled && (
                <div className="space-y-3 mt-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={config.heatmap.trackClicks}
                      onChange={(e) => setConfig({ ...config, heatmap: { ...config.heatmap, trackClicks: e.target.checked } })}
                      className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('sdk.advancedConfig.trackClicks')}</span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={config.heatmap.trackScroll}
                      onChange={(e) => setConfig({ ...config, heatmap: { ...config.heatmap, trackScroll: e.target.checked } })}
                      className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('sdk.advancedConfig.trackScrollDepth')}</span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={config.heatmap.trackMouseMovement}
                      onChange={(e) => setConfig({ ...config, heatmap: { ...config.heatmap, trackMouseMovement: e.target.checked } })}
                      className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('sdk.advancedConfig.trackMouseMovement')}</span>
                  </label>

                  {config.heatmap.trackMouseMovement && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Mouse Movement Sampling: {(config.heatmap.mouseMoveSampling * 100).toFixed(0)}%
                      </label>
                      <input
                        type="range"
                        min="0.05"
                        max="0.5"
                        step="0.05"
                        value={config.heatmap.mouseMoveSampling}
                        onChange={(e) => setConfig({ ...config, heatmap: { ...config.heatmap, mouseMoveSampling: parseFloat(e.target.value) } })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('sdk.advancedConfig.lowSamplingRecommended')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Performance & Errors - Compact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border-l-4 border-green-500 pl-4 bg-green-50/50 dark:bg-green-900/10 py-3 rounded-r-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">⚡ Performance</h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{t('sdk.advancedConfig.coreWebVitalsNetwork')}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.performance.captureWebVitals}
                      onChange={(e) => setConfig({ ...config, performance: { ...config.performance, captureWebVitals: e.target.checked } })}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
                  </label>
                </div>
              </div>

              <div className="border-l-4 border-red-500 pl-4 bg-red-50/50 dark:bg-red-900/10 py-3 rounded-r-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">🐛 Error Tracking</h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{t('sdk.advancedConfig.automaticErrorCapture')}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.errors.captureErrors}
                      onChange={(e) => setConfig({ ...config, errors: { ...config.errors, captureErrors: e.target.checked } })}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-red-300 dark:peer-focus:ring-red-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-red-600"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Installation Method Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className="flex space-x-1 p-2">
              <button
                onClick={() => setInstallMethod('cdn')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  installMethod === 'cdn'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <CodeBracketIcon className="w-5 h-5" />
                <span>{t('sdk.cdnRecommended')}</span>
              </button>
              <button
                onClick={() => setInstallMethod('npm')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  installMethod === 'npm'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <CommandLineIcon className="w-5 h-5" />
                <span>{t('sdk.npm')}</span>
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Step 1: Installation */}
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-600 text-white rounded-full font-bold text-sm shadow-md">
                  1
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('sdk.step1')}: {installMethod === 'npm' ? t('sdk.installPackage') : t('sdk.addScriptTag')}
                </h3>
              </div>
              <CodeBlock
                code={installMethod === 'npm' ? npmInstall : cdnInstall}
                index={0}
                language={installMethod === 'npm' ? 'bash' : 'html'}
              />

              {/* Performance Note for CDN Async Loader */}
              {installMethod === 'cdn' && (
                <>
                  <div className="mt-4 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 border-l-4 border-cyan-500 p-4 rounded-r-lg">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <svg className="w-6 h-6 text-cyan-600 dark:text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-cyan-900 dark:text-cyan-100 mb-1">
                          ✨ {t('sdk.allInOne.title')}
                        </h4>
                        <p className="text-xs text-cyan-800 dark:text-cyan-200 leading-relaxed">
                          {t('sdk.allInOne.description')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-l-4 border-green-500 p-4 rounded-r-lg">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-1">
                          🚀 {t('sdk.asyncLoader.title')}
                        </h4>
                        <p className="text-xs text-green-800 dark:text-green-200 leading-relaxed mb-2">
                          {t('sdk.asyncLoader.description')}
                        </p>
                        <ul className="text-xs text-green-800 dark:text-green-200 space-y-1">
                          <li>✅ {t('sdk.asyncLoader.feature1')}</li>
                          <li>✅ {t('sdk.asyncLoader.feature2')}</li>
                          <li>✅ {t('sdk.asyncLoader.feature3')}</li>
                          <li>✅ {t('sdk.asyncLoader.feature4')}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Step 2: Initialize (NPM only) */}
            {installMethod === 'npm' && (
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 text-white rounded-full font-bold text-sm shadow-md">
                    2
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('sdk.step2')}: {t('sdk.initializeSDK')}
                  </h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {t('sdk.importAndInit')}
                </p>
                <CodeBlock code={initCode} index={1} />

                {/* Important Note about apiUrl */}
                <div className="mt-4 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-l-4 border-blue-500 p-4 rounded-r-lg">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                        ⚠️ {t('sdk.apiUrlNote.title')}
                      </h4>
                      <p className="text-xs text-blue-800 dark:text-blue-200 leading-relaxed">
                        {t('sdk.apiUrlNote.description')} <code className="bg-blue-100 dark:bg-blue-800 px-1.5 py-0.5 rounded font-mono text-xs">https://api-monitoring.devskin.com</code>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2/3: Track Events */}
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-full font-bold text-sm shadow-md">
                  {installMethod === 'npm' ? '3' : '2'}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t(`sdk.step${installMethod === 'npm' ? '3' : '2'}`)}: {t('sdk.trackCustomEvents')}
                </h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {t('sdk.trackUserInteractions')}
              </p>
              <CodeBlock code={trackEventExample} index={2} />
            </div>

            {/* Step 3/4: Error Tracking */}
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-full font-bold text-sm shadow-md">
                  {installMethod === 'npm' ? '4' : '3'}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t(`sdk.step${installMethod === 'npm' ? '4' : '3'}`)}: {t('sdk.errorTracking')}
                </h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {t('sdk.errorsAutoCaptured')}
              </p>
              <CodeBlock code={errorTrackingExample} index={3} />
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 border border-cyan-200 dark:border-cyan-800 rounded-xl shadow-sm p-5 hover:shadow-md transition-all">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">🎯</span>
              <h4 className="font-semibold text-gray-900 dark:text-white">
                {t('sdk.features.eventTracking')}
              </h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('sdk.features.eventTrackingDesc')}
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800 rounded-xl shadow-sm p-5 hover:shadow-md transition-all">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">🎥</span>
              <h4 className="font-semibold text-gray-900 dark:text-white">
                {t('sdk.features.sessionReplay')}
              </h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('sdk.features.sessionReplayDesc')}
            </p>
            <div className="mt-2 inline-flex items-center gap-1 text-xs text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/30 px-2 py-1 rounded">
              <span>⚡</span>
              <span>{t('sdk.rrweb.poweredBy')}</span>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl shadow-sm p-5 hover:shadow-md transition-all">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">⚡</span>
              <h4 className="font-semibold text-gray-900 dark:text-white">
                {t('sdk.features.performance')}
              </h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('sdk.features.performanceDesc')}
            </p>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 border border-red-200 dark:border-red-800 rounded-xl shadow-sm p-5 hover:shadow-md transition-all">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">🐛</span>
              <h4 className="font-semibold text-gray-900 dark:text-white">
                {t('sdk.features.errorTracking')}
              </h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('sdk.features.errorTrackingDesc')}
            </p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl shadow-sm p-5 hover:shadow-md transition-all">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">🌐</span>
              <h4 className="font-semibold text-gray-900 dark:text-white">
                {t('sdk.features.networkMonitoring')}
              </h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('sdk.features.networkMonitoringDesc')}
            </p>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-900/20 dark:to-slate-900/20 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-5 hover:shadow-md transition-all">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">🔒</span>
              <h4 className="font-semibold text-gray-900 dark:text-white">
                {t('sdk.features.privacyFirst')}
              </h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('sdk.features.privacyFirstDesc')}
            </p>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border border-orange-200 dark:border-orange-800 rounded-xl shadow-sm p-5 hover:shadow-md transition-all">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">🗺️</span>
              <h4 className="font-semibold text-gray-900 dark:text-white">
                {t('sdk.rrweb.heatmaps')}
              </h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('sdk.rrweb.heatmapsDesc')}
            </p>
          </div>

          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 border border-teal-200 dark:border-teal-800 rounded-xl shadow-sm p-5 hover:shadow-md transition-all">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">📍</span>
              <h4 className="font-semibold text-gray-900 dark:text-white">
                {t('sdk.rrweb.geolocation')}
              </h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('sdk.rrweb.geolocationDesc')}
            </p>
          </div>
        </div>

        {/* Advanced Configuration */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
            <BookOpenIcon className="w-6 h-6 text-cyan-600" />
            <span>{t('sdk.advancedConfig.title')}</span>
          </h2>

          <div className="space-y-6">
            {/* Session Recording Config */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('sdk.advancedConfig.sessionRecording')}
                </h3>
                <span className="text-xs bg-purple-600 text-white px-2 py-1 rounded-full">{t('sdk.rrweb.poweredBy')}</span>
              </div>
              <div className="mb-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                <p className="text-sm text-purple-900 dark:text-purple-100 mb-2">
                  <strong>✨ {t('sdk.rrweb.config.title')}</strong>
                </p>
                <p className="text-xs text-purple-700 dark:text-purple-300">
                  {t('sdk.rrweb.config.description')}
                </p>
              </div>
              <CodeBlock
                code={`DevSkin.init({
  apiKey: '${apiKey}',
  appId: '${appId}',
  apiUrl: 'https://api-monitoring.devskin.com',

  sessionRecording: {
    enabled: true,

    // Privacy options (rrweb privacy features)
    maskAllInputs: true,           // Mask all input fields (passwords, emails, etc)
    maskTextSelector: '[data-private]', // CSS selector for sensitive text
    blockClass: 'rr-block',        // Elements with this class won't be recorded
    ignoreClass: 'rr-ignore',      // Ignore specific elements

    // Sampling (reduce bandwidth & storage)
    sampling: 1.0,                 // Record 50% of sessions

    // rrweb Performance Options
    recordCanvas: false,           // Record canvas elements (resource intensive)
    recordCrossOriginIframes: false, // Don't record cross-origin iframes

    // Advanced rrweb features
    collectFonts: true,            // Collect fonts for better replay
    inlineStylesheet: true,        // Inline stylesheets for consistent replay
    checkoutEveryNms: 300000,      // Full snapshot every 5 minutes
    checkoutEveryNth: 200,         // Full snapshot every 200 events
  },
});

// Storage: Recordings are saved to S3 automatically
// Playback: View recordings at /analytics/journeys with full controls`}
                index={10}
              />
            </div>

            {/* Heatmap Config */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center space-x-2">
                <span className="text-xl">🗺️</span>
                <span>Heatmap & Comportamento do Usuário</span>
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Colete dados de cliques, scroll e movimento do mouse para análise de comportamento.
              </p>
              <CodeBlock
                code={`DevSkin.init({
  apiKey: '${apiKey}',
  appId: '${appId}',
  apiUrl: 'https://api-monitoring.devskin.com',

  // Heatmap & User Behavior Tracking
  heatmapOptions: {
    enabled: true,

    // Click heatmap
    trackClicks: true,             // Track all user clicks

    // Scroll depth tracking
    trackScroll: true,              // Track how far users scroll

    // Mouse movement (pode consumir mais recursos)
    trackMouseMovement: true,      // Track mouse hover patterns
    mouseMoveSampling: 0.1,         // Sample 10% of mouse movements
  },
});`}
                index={15}
              />
              <div className="mt-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>⚠️ Nota:</strong> O rastreamento de movimento do mouse pode consumir mais banda e processamento.
                  Use <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">trackMouseMovement: true</code> ou
                  ajuste o <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">mouseMoveSampling</code> para valores baixos (0.05 - 0.2)
                  para otimizar performance.
                </p>
              </div>
            </div>

            {/* Performance Monitoring Config */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('sdk.advancedConfig.performanceMonitoring')}
              </h3>
              <CodeBlock
                code={`DevSkin.init({
  apiKey: '${apiKey}',
  appId: '${appId}',
  apiUrl: 'https://api-monitoring.devskin.com',

  // Capture Core Web Vitals automatically
  captureWebVitals: true,

  // Monitor network requests
  captureNetworkRequests: true,
  networkRequestOptions: {
    // Ignore specific URLs
    ignoreUrls: [/analytics\\.google\\.com/],

    // Capture request/response bodies
    captureHeaders: true,
    captureBody: false, // Be careful with sensitive data

    // Failed requests only
    captureFailedOnly: false,
  },

  // Long task detection
  longTaskThreshold: 50, // Report tasks longer than 50ms
});`}
                index={11}
              />
            </div>

            {/* Error Tracking Config */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('sdk.advancedConfig.errorTrackingConfig')}
              </h3>
              <CodeBlock
                code={`DevSkin.init({
  apiKey: '${apiKey}',
  appId: '${appId}',
  apiUrl: 'https://api-monitoring.devskin.com',

  captureErrors: true,

  errorOptions: {
    // Ignore specific errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      /timeout/i,
    ],

    // Ignore errors from specific URLs
    denyUrls: [
      /extensions\\//i,
      /^chrome:\\/\\//i,
    ],

    // Include local variables in stack trace
    includeLocalVariables: false,

    // Max breadcrumbs to keep
    maxBreadcrumbs: 100,
  },

  // Before sending errors (filter/modify)
  beforeSend(event) {
    // Don't send errors in development
    if (window.location.hostname === 'localhost') {
      return null;
    }

    // Redact sensitive data
    if (event.extra?.password) {
      delete event.extra.password;
    }

    return event;
  },
});`}
                index={12}
              />
            </div>

            {/* React Integration */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('sdk.advancedConfig.reactIntegration')}
              </h3>
              <CodeBlock
                code={`import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import DevSkin from '@devskin/browser-sdk';

function App() {
  const location = useLocation();

  // Track route changes
  useEffect(() => {
    DevSkin.trackPageView({
      path: location.pathname,
      search: location.search,
      title: document.title,
    });
  }, [location]);

  return <YourApp />;
}

// Error Boundary Integration
class ErrorBoundary extends React.Component {
  componentDidCatch(error, errorInfo) {
    DevSkin.captureError(error, {
      tags: { boundary: 'root' },
      extra: { errorInfo },
    });
  }

  render() {
    return this.props.children;
  }
}`}
                index={13}
              />
            </div>

            {/* Vue Integration */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('sdk.advancedConfig.vueIntegration')}
              </h3>
              <CodeBlock
                code={`import { createApp } from 'vue';
import DevSkin from '@devskin/browser-sdk';
import App from './App.vue';

const app = createApp(App);

// Global error handler
app.config.errorHandler = (err, instance, info) => {
  DevSkin.captureError(err, {
    tags: { vue: true },
    extra: { componentName: instance?.$options.name, info },
  });
};

// Track route changes
router.afterEach((to, from) => {
  DevSkin.trackPageView({
    path: to.path,
    title: to.meta.title || document.title,
  });
});

app.mount('#app');`}
                index={14}
              />
            </div>
          </div>
        </div>

        {/* API Reference */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
            <AcademicCapIcon className="w-6 h-6 text-purple-600" />
            <span>{t('sdk.apiReference.title')}</span>
          </h2>

          <div className="space-y-4">
            <div className="border-l-4 border-cyan-500 pl-4 bg-cyan-50/50 dark:bg-cyan-900/10 py-3 rounded-r-lg">
              <h4 className="font-semibold text-gray-900 dark:text-white font-mono text-sm">DevSkin.track(eventName, properties?)</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t('sdk.apiReference.track')}
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4 bg-purple-50/50 dark:bg-purple-900/10 py-3 rounded-r-lg">
              <h4 className="font-semibold text-gray-900 dark:text-white font-mono text-sm">DevSkin.identify(userId, traits?)</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t('sdk.apiReference.identify')}
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4 bg-blue-50/50 dark:bg-blue-900/10 py-3 rounded-r-lg">
              <h4 className="font-semibold text-gray-900 dark:text-white font-mono text-sm">DevSkin.trackPageView(properties?)</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t('sdk.apiReference.trackPageView')}
              </p>
            </div>

            <div className="border-l-4 border-red-500 pl-4 bg-red-50/50 dark:bg-red-900/10 py-3 rounded-r-lg">
              <h4 className="font-semibold text-gray-900 dark:text-white font-mono text-sm">DevSkin.captureError(error, context?)</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t('sdk.apiReference.captureError')}
              </p>
            </div>

            <div className="border-l-4 border-green-500 pl-4 bg-green-50/50 dark:bg-green-900/10 py-3 rounded-r-lg">
              <h4 className="font-semibold text-gray-900 dark:text-white font-mono text-sm">DevSkin.addBreadcrumb(breadcrumb)</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t('sdk.apiReference.addBreadcrumb')}
              </p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4 bg-yellow-50/50 dark:bg-yellow-900/10 py-3 rounded-r-lg">
              <h4 className="font-semibold text-gray-900 dark:text-white font-mono text-sm">DevSkin.setUser(user) / DevSkin.clearUser()</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t('sdk.apiReference.setUser')}
              </p>
            </div>

            <div className="border-l-4 border-pink-500 pl-4 bg-pink-50/50 dark:bg-pink-900/10 py-3 rounded-r-lg">
              <h4 className="font-semibold text-gray-900 dark:text-white font-mono text-sm">DevSkin.startRecording() / DevSkin.stopRecording()</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t('sdk.apiReference.startRecording')}
              </p>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {t('sdk.troubleshooting.title')}
          </h2>

          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                {t('sdk.troubleshooting.notSeeingData')}
              </h4>
              <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <li>{t('sdk.troubleshooting.notSeeingDataList1')}</li>
                <li>{t('sdk.troubleshooting.notSeeingDataList2')}</li>
                <li>
                  {t('sdk.troubleshooting.notSeeingDataList3')} <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">debug: true</code>
                </li>
                <li>{t('sdk.troubleshooting.notSeeingDataList4')}</li>
                <li>{t('sdk.troubleshooting.notSeeingDataList5')}</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                {t('sdk.troubleshooting.recordingsNotWorking')}
              </h4>
              <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <li>
                  {t('sdk.troubleshooting.recordingsNotWorkingList1')} <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">sessionRecording.enabled: true</code>
                </li>
                <li>{t('sdk.troubleshooting.recordingsNotWorkingList2')}</li>
                <li>{t('sdk.troubleshooting.recordingsNotWorkingList3')}</li>
                <li>{t('sdk.troubleshooting.recordingsNotWorkingList4')}</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                {t('sdk.troubleshooting.highBandwidth')}
              </h4>
              <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <li>{t('sdk.troubleshooting.highBandwidthList1')}</li>
                <li>
                  {t('sdk.troubleshooting.highBandwidthList2')} <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">recordCanvas: false</code>
                </li>
                <li>{t('sdk.troubleshooting.highBandwidthList3')}</li>
                <li>{t('sdk.troubleshooting.highBandwidthList4')}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/analytics/events"
            className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl p-6 text-white hover:shadow-lg transition-all group relative overflow-hidden"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold">{t('sdk.quickLinks.viewEvents')}</h3>
                <ArrowTopRightOnSquareIcon className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </div>
              <p className="text-cyan-50 text-sm">
                {t('sdk.quickLinks.viewEventsDesc')}
              </p>
            </div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -z-0" />
          </Link>

          <Link
            to="/analytics/funnels"
            className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-6 text-white hover:shadow-lg transition-all group relative overflow-hidden"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold">{t('sdk.quickLinks.createFunnels')}</h3>
                <ArrowTopRightOnSquareIcon className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </div>
              <p className="text-purple-50 text-sm">
                {t('sdk.quickLinks.createFunnelsDesc')}
              </p>
            </div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -z-0" />
          </Link>

          <Link
            to="/rum/sessions"
            className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6 text-white hover:shadow-lg transition-all group relative overflow-hidden"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold">{t('sdk.quickLinks.sessionReplays')}</h3>
                <ArrowTopRightOnSquareIcon className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </div>
              <p className="text-green-50 text-sm">
                {t('sdk.quickLinks.sessionReplaysDesc')}
              </p>
            </div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -z-0" />
          </Link>

          <Link
            to="/analytics/journeys"
            className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl p-6 text-white hover:shadow-lg transition-all group relative overflow-hidden"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold">{t('sdk.rrweb.quickLinks.heatmaps')}</h3>
                <ArrowTopRightOnSquareIcon className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </div>
              <p className="text-orange-50 text-sm">
                {t('sdk.rrweb.quickLinks.heatmapsDesc')}
              </p>
            </div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -z-0" />
          </Link>
        </div>

        {/* Documentation Links */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 dark:from-gray-700 dark:to-gray-800 rounded-xl p-6 text-white relative overflow-hidden">
          <div className="relative z-10">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-xl font-semibold mb-2 flex items-center space-x-2">
                  <BookOpenIcon className="w-6 h-6" />
                  <span>{t('sdk.needHelp.title')}</span>
                </h3>
                <p className="text-gray-300 mb-4">
                  {t('sdk.needHelp.description')}
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://github.com/devskin1/devskin-monitoring-browser-sdk#readme"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors backdrop-blur-sm"
                  >
                    <BookOpenIcon className="w-4 h-4" />
                    <span>{t('sdk.needHelp.fullDocs')}</span>
                    <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-full blur-3xl -z-0" />
        </div>
        </>
        )}
      </div>
    </MainLayout>
  );
}
